// The month: a calendar of this account's own schedules.
//
// NOT A CALENDAR FEED. Nothing on this screen comes from Google Calendar, and
// nothing ever will: reading somebody's calendar needs a Google *sensitive*
// scope, which forfeits the verification exemption sign-in rests on and caps the
// product at 100 users until verification completes (AGENTS.md, "No calendar
// overlay"). What is drawn here is `routine` and `completion`, expanded from
// recurrence rules at read time.
//
// A SQUARE IS A COUNT AND THE PANEL UNDER IT IS THE DAY. The grid answers "what
// does this month look like" - how much is due, which schedules, and how much is
// finished - and opening a square asks `GET /days/:day` for what is actually on
// it. That is the same endpoint the day screen reads, so a square and the day
// behind it cannot disagree.
//
// IT WRITES NOTHING, and says nothing about writing. Ticking lives on the day
// screen, which reaches yesterday, today and tomorrow and no further - so a line
// here telling somebody to tick the 23rd off on Today would be a direction to a
// place they cannot get to, which is precisely the trap this client has been
// caught by five times (web/src/README.md). The one control that does point at
// the day screen, `Open on Today`, is drawn ONLY for the three days it can
// actually show, and it names the day it will open.

import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import type { DayChoice } from '../day/DayScreen.tsx';
import type { DayResponse, IsoDay, MonthDay, MonthResponse } from '../api/client.ts';
import { itemDone, stepsDone } from '../day/day-model.ts';
import { Icon } from '../day/Icon.tsx';
import { hueIn } from '../edit/edit-model.ts';
import { dayAndMonth, monthAndYear, weekdayName } from '../iso-day.ts';
import type { Mode } from '../shell/theme.ts';
import { useMode } from '../shell/theme.ts';
import {
  choiceFor,
  dotsOn,
  emptyMonthBody,
  gridCells,
  isFuture,
  monthSummary,
  squareLabel,
  WEEKDAY_INITIALS,
} from './month-model.ts';
import type { MonthScreenState } from './useMonthScreen.ts';

interface Props {
  state: MonthScreenState;
  /** Out of a screen that is not a tab - see the header below. */
  onBack: () => void;
  /**
   * Open one of the day screen's three days. Given a choice rather than a day,
   * because those three are all it can be asked for - see `choiceFor`.
   */
  onOpenDay: (choice: DayChoice) => void;
}

export function MonthScreen({ state, onBack, onOpenDay }: Props) {
  const { view } = state;

  return (
    <>
      {/* The way out of a screen that is not a tab, which is the shape Settings
          takes and for the same reason: the tab bar below cannot answer for a
          screen it does not list, so this is the only way back. It is here at
          both widths rather than at one, because the sidebar cannot answer for
          it either. Deliberately not `history.back()` - `useShell.back` says
          why, and the short version is that a linkable screen can be the first
          entry in a tab's history. */}
      <div className="topbar">
        <button className="iconbtn" aria-label="Back" onClick={onBack}>
          <Icon name="chevL" />
        </button>
        {/* The screen's own name, at the top of its heading order. The MONTH is
            an h2 below, because which month is on is a thing this screen
            changes and the screen is still the same screen. */}
        <h1>Month</h1>
        {/* The heading is centred by the two controls either side of it, and
            there is only one. The prototype balances it with an empty box of
            the same size rather than letting the title drift left. */}
        <span className="iconbtn" aria-hidden="true" />
      </div>

      <div className="scroll">
        <div className="wrap">
          {/* MOUNTED WHETHER OR NOT THE MONTH ARRIVED, which is the same rule
              the day screen states for its own chooser ("the chooser is the
              only way back to a day already read, so a neighbouring day that is
              slow or unreachable must never take it away") - written there as a
              comment, and therefore not inherited by this screen when it was
              built. It is a shared invariant rather than that component's
              habit: a September that could not be read must not remove the
              arrow back to the August that was. */}
          <MonthBar state={state} />

          {/* The month is still drawn and a later read of it did not arrive, so
              this says WHICH of the two you are looking at rather than replacing
              a grid that is very likely still right - the shape Settings already
              uses for its own list, so this is one pattern and not two. It takes
              no control away: the arrows, "This month", every square and the open
              panel all still work, which is the whole point of keeping them. */}
          {state.refreshFailure !== null && (
            <p className="sheetproblem" role="alert">
              This month may be out of date. {state.refreshFailure}
            </p>
          )}

          {view === null ? (
            // Whether a month is on its way is a state of its own, never
            // inferred from the absence of a reason it is not: a screen with no
            // month, nothing in flight and no way to ask again is the state to
            // avoid.
            <div className="daystate" role="status">
              {state.loading ? (
                <p>Loading your month…</p>
              ) : (
                <>
                  <h2>That month is not here.</h2>
                  <p>{state.failure}</p>
                  <button className="btn" onClick={state.retry}>
                    Try again
                  </button>
                </>
              )}
            </div>
          ) : (
            <MonthBody state={state} view={view} onOpenDay={onOpenDay} />
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Which month is on, and the way to the two either side of it.
 *
 * DRAWN FROM THE MONTH BEING ASKED FOR rather than from the one that loaded, so
 * a month whose payload never arrived is named honestly and is still somewhere
 * you can leave. Where the payload IS here, its own `previousMonth` /
 * `nextMonth` are what the arrows follow - the server stays the authority the
 * moment it has spoken, and `addMonths` is only the fallback (`useMonthScreen`).
 */
function MonthBar({ state }: { state: MonthScreenState }) {
  const { month, previousMonth, nextMonth, thisMonth } = state;

  // These three arrive together: the neighbours are derived from the month, so
  // the only state without them is the one before anything at all has come back
  // - the first read, and the failure of the first read. There is no month to
  // name there and no neighbour to step to, and nothing already read for a
  // failure to take away either, so "Try again" below is the whole of the
  // honest answer.
  if (month === null || previousMonth === null || nextMonth === null) return null;

  return (
    <>
      {/* Both arrows carry the month they go to in their name: "Previous" alone
          makes a screen reader announce the same word on every month of the
          year, and the whole point of the control is which one it lands on. */}
      <div className="monthbar">
        <button
          className="iconbtn"
          aria-label={`Go to ${monthAndYear(`${previousMonth}-01`)}`}
          onClick={() => state.go(previousMonth)}
        >
          <Icon name="chevL" />
        </button>
        {/* Which month is on, under the screen's own h1 in the header. Serif,
            because the date on the day screen is (prototype-v1.md section 4). */}
        <h2 className="monthname">{monthAndYear(`${month}-01`)}</h2>
        <button
          className="iconbtn"
          aria-label={`Go to ${monthAndYear(`${nextMonth}-01`)}`}
          onClick={() => state.go(nextMonth)}
        >
          <Icon name="chevR" />
        </button>
      </div>

      {/* Only when you are somewhere else, because a control that returns you to
          where you already are is a control that appears to do nothing.

          "This month" and not the month's name, which is the vocabulary the day
          screen's chooser already uses for the same idea - a button reading
          "August 2026" beside a heading reading "July 2026" is read as a picker
          rather than as the way back. Its accessible name carries the month, so
          nothing is lost to somebody who cannot see the heading. */}
      {thisMonth !== null && month !== thisMonth && (
        <div className="monthback">
          <button
            className="btn"
            aria-label={`This month, ${monthAndYear(`${thisMonth}-01`)}`}
            onClick={() => state.go(thisMonth)}
          >
            This month
          </button>
        </div>
      )}
    </>
  );
}

function MonthBody({
  state,
  view,
  onOpenDay,
}: {
  state: MonthScreenState;
  view: MonthResponse;
  onOpenDay: (choice: DayChoice) => void;
}) {
  const summary = monthSummary(view);
  const cells = gridCells(view);
  // The stored hex is the light one; on a dark page the legend reads the
  // prototype's dark counterpart instead (`edit/edit-model.ts`).
  const mode = useMode();

  return (
    <>
      {/* The bar naming this month and the two either side of it is ABOVE this,
          in `MonthBar`, mounted whether or not a month arrived - see the note
          there. What is left here is everything that needs a payload. */}

      {view.progress.total > 0 && (
        <div className="prog">
          {/* A COUNT AND NEVER A BAR. `total` includes the days that have not
              happened yet, so a bar would draw a month two thirds ahead of
              itself as two thirds failed - and a figure that only grows is what
              this product shows instead of a streak (AGENTS.md). A month
              entirely ahead of today has no count at all, exactly as a future
              day on the day screen has no bar. */}
          {summary.done !== null && (
            <div className="prog-row">
              <span className="prog-n tnum">{summary.done} done</span>
            </div>
          )}
          <div className="dayline">{summary.line}</div>
        </div>
      )}

      <div className="mgrid" role="group" aria-label={`Days in ${monthAndYear(view.firstDay)}`}>
        {WEEKDAY_INITIALS.map((initial, column) => (
          // Two S's and two T's, so these are decoration: each square carries
          // its own weekday in its accessible name instead.
          <span className="mdow" key={column} aria-hidden="true">
            {initial}
          </span>
        ))}

        {cells.map((cell, index) =>
          cell === null ? (
            <span className="mcell blank" key={`blank-${index}`} aria-hidden="true" />
          ) : (
            <Square
              key={cell.day}
              cell={cell}
              view={view}
              mode={mode}
              open={state.selected === cell.day}
              onOpen={() => state.select(cell.day)}
            />
          ),
        )}
      </div>

      {view.progress.total === 0 && (
        <div className="mempty">
          {/* An h3 under the month's own h2, so the heading order has no gap in
              it - the same rule the placeholders and Settings follow. */}
          <h3>A clear month.</h3>
          <p>{emptyMonthBody(view)}</p>
        </div>
      )}

      {/* ABOVE the legend, which is reference and can wait: the open day is the
          thing that was just asked for, and it belongs directly under the square
          that asked for it. */}
      {state.selected !== null && (
        <DayPanel
          key={state.selected}
          day={state.selected}
          view={view}
          detail={state.day}
          loading={state.dayLoading}
          failure={state.dayFailure}
          refreshFailure={state.dayRefreshFailure}
          onRetry={state.retryDay}
          onClose={() => state.select(null)}
          onOpenDay={onOpenDay}
        />
      )}

      {view.sections.length > 0 && (
        <ul className="mlegend">
          {view.sections.map((section) => (
            <li key={section.id} style={{ '--c': hueIn(section.colorHex, mode) } as CSSProperties}>
              <span className="mdot" />
              {section.name}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** One day of the month: its number, what is due in it, and how much is done. */
function Square({
  cell,
  view,
  mode,
  open,
  onOpen,
}: {
  cell: MonthDay;
  view: MonthResponse;
  /** Resolved once by `MonthBody`: a grid mounts one subscription, not 42. */
  mode: Mode;
  open: boolean;
  onOpen: () => void;
}) {
  const { shown, more } = dotsOn(cell, view);
  const finished = cell.total > 0 && cell.done === cell.total;

  const classes = [
    'mcell',
    cell.total === 0 ? 'quiet' : '',
    // A finished day is filled, and it is not colour alone: the number goes
    // solid with it, which is the same rule the day screen's rows follow.
    finished && !isFuture(cell, view) ? 'full' : '',
    cell.day === view.today ? 'now' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={classes}
      aria-pressed={open}
      aria-label={squareLabel(cell, view)}
      onClick={onOpen}
    >
      <span className="mnum tnum" aria-hidden="true">
        {Number(cell.day.slice(8))}
      </span>
      <span className="mdots" aria-hidden="true">
        {shown.map((section) => (
          <span
            className="mdot"
            key={section.id}
            style={{ '--c': hueIn(section.colorHex, mode) } as CSSProperties}
          />
        ))}
        {more > 0 && <span className="mmore tnum">+{more}</span>}
      </span>
      {/* Ink measures, section colour identifies - the dots above say WHICH
          schedules and this says how much of the day is closed out. A future day
          has none of it, exactly as a future day on the day screen has no bar:
          a filled bar for work nobody has reached yet reads as failure rather
          than as ahead. */}
      {cell.total > 0 && !isFuture(cell, view) && (
        <span className="mbar" aria-hidden="true">
          <i style={{ width: `${Math.round((cell.done / cell.total) * 100)}%` }} />
        </span>
      )}
    </button>
  );
}

/**
 * What is actually on the open square - read from `GET /days/:day`, the day
 * screen's own endpoint, so the two can never disagree about a day.
 *
 * It is a READING of the day rather than a second copy of it: no checkboxes, no
 * sheets, nothing that writes. What it offers instead is the day screen itself,
 * and only for the three days that screen can show.
 */
function DayPanel({
  day,
  view,
  detail,
  loading,
  failure,
  refreshFailure,
  onRetry,
  onClose,
  onOpenDay,
}: {
  day: IsoDay;
  view: MonthResponse;
  detail: DayResponse | null;
  loading: boolean;
  failure: string | null;
  refreshFailure: string | null;
  onRetry: () => void;
  onClose: () => void;
  onOpenDay: (choice: DayChoice) => void;
}) {
  const choice = choiceFor(day, view.today);
  const panel = useRef<HTMLDivElement>(null);
  const mode = useMode();

  // BROUGHT INTO VIEW, because a square in the top row is thirty squares above
  // this. Opening one and seeing nothing move is the trap this client has been
  // caught by five times wearing yet another hat: an action that succeeds while
  // showing nothing reads as a failure, and the first thing anybody does with
  // one is press it again.
  //
  // `nearest` scrolls the least it can, so a panel already on screen does not
  // move at all - which is what makes opening a second square from the bottom
  // rows feel like nothing happened, correctly. Optional-called because jsdom
  // has no layout and therefore no `scrollIntoView`.
  useEffect(() => {
    panel.current?.scrollIntoView?.({ block: 'nearest' });
  }, [day]);

  return (
    <div className="mday" ref={panel}>
      <div className="mday-head">
        {/* An h3 under the month's h2: the open square is a section of this
            screen, not a screen of its own. */}
        <h3>
          <span className="mday-w">{weekdayName(day)}</span>
          <span className="mday-d">{dayAndMonth(day)}</span>
        </h3>
        <button className="iconbtn" aria-label={`Close ${dayAndMonth(day)}`} onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>

      {detail === null ? (
        <div className="daystate" role="status">
          {loading ? (
            <p>Loading that day…</p>
          ) : (
            <>
              <p>{failure}</p>
              <button className="btn" onClick={onRetry}>
                Try again
              </button>
            </>
          )}
        </div>
      ) : detail.sections.length === 0 ? (
        <p className="finetext">Nothing is due on this day.</p>
      ) : (
        <ul className="mlist">
          {detail.sections.map((section) => (
            <li key={section.id} style={{ '--c': hueIn(section.colorHex, mode) } as CSSProperties}>
              <div className="mlist-head">
                <span className="chip">
                  <Icon name={section.symbol} />
                </span>
                <b>{section.name}</b>
                <span className="mlist-n tnum">
                  {section.done}/{section.total}
                </span>
              </div>
              <ul>
                {section.items.map((item) => (
                  <li key={item.id} className={itemDone(item) ? 'done' : ''}>
                    {/* The mark is the day screen's tick, drawn and not
                        pressable - this panel writes nothing. The state is SAID
                        as well as drawn, because there is no checkbox here for a
                        screen reader to read it off, and a filled circle beside
                        a struck-through title is shape and colour only. */}
                    <span className="mtick" aria-hidden="true">
                      <i />
                    </span>
                    <span className="mtitle">{item.title}</span>
                    {/* A checklist's own figure, as the day screen draws it.
                        Without it an item with five steps and one done reads
                        exactly like one with nothing done - and the schedule's
                        count above says 0/1 either way, because five steps are
                        ONE thing to do (AGENTS.md). */}
                    {item.steps.length > 0 && (
                      <span className="mstep tnum">
                        {stepsDone(item)}/{item.steps.length}
                      </span>
                    )}
                    <span className="sr">{itemDone(item) ? ' - done' : ' - not done'}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      {/* Under the list, the way Settings puts it under its own, and for the
          same reason the grid keeps its stale line: what is drawn was right when
          it arrived, so it is worth more than the blank a failed refresh would
          leave. */}
      {refreshFailure !== null && (
        <p className="sheetproblem" role="alert">
          This day may be out of date. {refreshFailure}
        </p>
      )}

      {/* ONLY the three days the day screen can show. It names the day it opens,
          because a button that says "Open on Today" on a square marked 23 is a
          button whose destination is a guess. */}
      {choice !== null && (
        <button className="btn" onClick={() => onOpenDay(choice)}>
          Open {dayAndMonth(day)} on Today
        </button>
      )}
    </div>
  );
}
